/**
 * Wall-vertex graph + terminal/junction/corner classification for the COMBAT.app
 * holo map. This is the square-fenced render-layer geometry that lets the stage cap
 * free wall ends and dot junction/corner nodes, giving the painted edge-set an
 * "architectural wall" read instead of a scatter of disconnected segments.
 *
 * **Render-only, no mechanical meaning.** A wall's vertices are
 * a *drawing* concern — they carry no rule, no LoS, no movement effect. This module
 * derives them purely from the rendered wall segments (the same {@link edgeSegment}
 * the stage draws), so it never branches on grid mode and never reads a domain
 * cell's coordinates beyond the square render fence in {@link combatCells}. The
 * engine stays agnostic; this is the square-only surface's own geometry, the sibling
 * of {@link combatCells} (it shares the square fence and the unit-test pattern).
 *
 * The model: each rendered wall is a unit segment lying on a grid line, joining two
 * **lattice corners** (`{x,y}` in cell units — multiply by `CELL` for px, exactly as
 * the authoring `Vertex` does). Treating the corners as a graph, a corner's *degree*
 * is how many wall segments meet there, and its incident segments' *axes* (horizontal
 * vs vertical) say whether the wall passes straight through or turns:
 *
 *  - **degree 1** → a **terminal** (free end): cap it.
 *  - **degree ≥ 3** → a **junction** (a T or +): node it.
 *  - **degree 2, the two segments NOT collinear** → a **corner** (an L turn): node it.
 *  - **degree 2, collinear** → a straight pass-through: NOT a node (nothing to draw).
 */
import type { EdgeMap } from "@lancer-vtt/domain";
import { CELL, edgeSegment, parseEdgeCells } from "./combatCells";

/** How a wall-graph vertex reads visually: a free end, a multi-wall junction, or an L-turn corner. */
export type WallNodeKind = "terminal" | "junction" | "corner";

/** A classified wall-graph vertex at lattice-corner `{x,y}` (cell units; × `CELL` for px). */
export type WallNode = { x: number; y: number; kind: WallNodeKind };

/** Whether an edge's flags make it a *rendered* wall (a movement or sight blocker draws a segment). */
function isWall(flags: { blocksMovement?: boolean; blocksSight?: boolean }): boolean {
  return flags.blocksMovement === true || flags.blocksSight === true;
}

/**
 * Build the wall-vertex graph from the {@link EdgeMap} and classify every vertex that
 * earns a node — the **terminals** (degree-1 free ends), **junctions** (degree ≥ 3),
 * and **corners** (degree-2 turns). Straight pass-through vertices (degree-2 collinear)
 * are omitted: there is nothing to draw at a point where a wall simply continues.
 *
 * Pure (`EdgeMap` → nodes); the canonical edge keys are inverted to wall segments via
 * the {@link combatCells} square render fence, so the geometry agrees with what the
 * stage actually strokes. Non-orthogonal / non-rendered edges contribute nothing.
 */
export function wallNodes(edges: EdgeMap): WallNode[] {
  // corner key (`"cx,cy"`) → its position + the axes ("h"/"v") of every wall segment meeting there.
  const corners = new Map<string, { x: number; y: number; axes: ("h" | "v")[] }>();
  const touch = (cx: number, cy: number, axis: "h" | "v") => {
    const key = `${cx},${cy}`;
    const entry = corners.get(key) ?? { x: cx, y: cy, axes: [] };
    entry.axes.push(axis);
    corners.set(key, entry);
  };

  for (const [key, flags] of Object.entries(edges)) {
    if (!isWall(flags)) continue;
    const [a, b] = parseEdgeCells(key);
    const seg = edgeSegment(a, b);
    if (!seg) continue; // a non-orthogonal edge has no boundary segment, so no graph contribution
    const axis: "h" | "v" = seg.x1 === seg.x2 ? "v" : "h"; // the wall segment's own orientation
    touch(seg.x1 / CELL, seg.y1 / CELL, axis);
    touch(seg.x2 / CELL, seg.y2 / CELL, axis);
  }

  const nodes: WallNode[] = [];
  for (const { x, y, axes } of corners.values()) {
    const degree = axes.length;
    if (degree === 1) nodes.push({ x, y, kind: "terminal" });
    else if (degree >= 3) nodes.push({ x, y, kind: "junction" });
    else if (axes[0] !== axes[1]) nodes.push({ x, y, kind: "corner" }); // degree 2, turns → corner
    // degree 2 collinear (axes[0] === axes[1]) → straight pass-through, no node
  }
  return nodes;
}
